"use client"

import React, { useState, useRef, useEffect } from "react"
import { cn } from "../../lib/utils"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Phone, Globe, Check, X, Lock, Sparkles } from "lucide-react"
import { useSubscription } from "../../hooks/use-subscription"
import { Card, CardContent } from "../ui/card"
import { Button } from "../ui/button"
import { motion, AnimatePresence } from "framer-motion"

// Ülke kodları ve bayrakları
const countries = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸', format: '(xxx) xxx-xxxx' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧', format: 'xxxx xxxxxx' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷', format: '(xxx) xxx xx xx' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪', format: 'xxx xxxxxxxx' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', format: 'x xx xx xx xx' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹', format: 'xxx xxxxxxx' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸', format: 'xxx xxx xxx' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳', format: 'xxx xxxx xxxx' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵', format: 'xx xxxx xxxx' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷', format: 'xx xxxx xxxx' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳', format: 'xxxxx xxxxx' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷', format: '(xx) xxxxx-xxxx' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽', format: 'xxx xxx xxxx' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', format: '(xxx) xxx-xxxx' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺', format: 'xxx xxx xxx' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺', format: '(xxx) xxx-xx-xx' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱', format: 'x xxxxxxxx' },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪', format: 'xx xxx xx xx' },
  { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴', format: 'xxx xx xxx' },
  { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮', format: 'xx xxxxxxx' }
]

export interface PhoneNumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: {
    country: string
    number: string
  }
  onChange?: (value: { country: string; number: string; fullNumber: string; isValid: boolean }) => void
  defaultCountry?: string
  countries?: typeof countries
  showFlags?: boolean
  showDialCode?: boolean
  autoFormat?: boolean
  validateOnChange?: boolean
  showValidationIcon?: boolean
  label?: string
  error?: string
  helperText?: string
  required?: boolean
  className?: string
  inputClassName?: string
  selectClassName?: string
  labelClassName?: string
  errorClassName?: string
  allowInternationalFormat?: boolean
}

// Telefon numarası formatlama
function formatPhoneNumber(number: string, format: string): string {
  if (!number || !format) return ''
  const cleaned = number.replace(/\D/g, '')
  let formatted = ''
  let digitIndex = 0
  
  for (let i = 0; i < format.length && digitIndex < cleaned.length; i++) {
    if (format[i] === 'x') {
      formatted += cleaned[digitIndex]
      digitIndex++
    } else {
      formatted += format[i]
    }
  }
  
  return formatted
}

// Telefon numarası doğrulama
function validatePhoneNumber(number: string, countryCode: string): boolean {
  if (!number || !countryCode) return false
  const cleaned = number.replace(/\D/g, '')
  const country = countries.find(c => c.code === countryCode)
  
  if (!country) return false
  
  // Format'taki x sayısını say
  const expectedLength = country.format.split('').filter(c => c === 'x').length
  
  return cleaned.length === expectedLength
}

// Uluslararası format dönüştürme
function toInternationalFormat(countryCode: string, phoneNumber: string): string {
  if (!countryCode || !phoneNumber) return ''
  const country = countries.find(c => c.code === countryCode)
  if (!country) return phoneNumber
  
  const cleaned = phoneNumber.replace(/\D/g, '')
  return `${country.dialCode}${cleaned}`
}

const defaultValue = { country: 'US', number: '' }

// Internal PhoneNumberInput component with actual implementation
const PhoneNumberInputInternal = React.forwardRef<HTMLDivElement, PhoneNumberInputProps>(({
  value,
  onChange,
  defaultCountry = 'US',
  countries: customCountries,
  showFlags = true,
  showDialCode = true,
  autoFormat = true,
  validateOnChange = true,
  showValidationIcon = true,
  label,
  error,
  helperText,
  required,
  className,
  inputClassName,
  selectClassName,
  labelClassName,
  errorClassName,
  disabled,
  allowInternationalFormat = true,
  ...props
}, ref) => {
  // Stable default value
  const phoneValue = React.useMemo(() => {
    return value || defaultValue
  }, [value])
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  
  const countryList = customCountries || countries
  const selectedCountry = countryList.find(c => c.code === phoneValue.country) || countryList[0]

  // Doğrulama - useEffect yerine doğrudan hesapla
  const isValid = validateOnChange && phoneValue.number 
    ? validatePhoneNumber(phoneValue.number, phoneValue.country)
    : false

  const handleCountryChange = (countryCode: string) => {
    const fullNumber = allowInternationalFormat 
      ? toInternationalFormat(countryCode, phoneValue.number)
      : phoneValue.number
      
    onChange?.({
      country: countryCode,
      number: phoneValue.number,
      fullNumber,
      isValid: validatePhoneNumber(phoneValue.number, countryCode)
    })
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value
    
    if (autoFormat) {
      // Sadece sayıları al
      const cleaned = newValue.replace(/\D/g, '')
      // Format uygula
      newValue = formatPhoneNumber(cleaned, selectedCountry.format)
    }
    
    const valid = validatePhoneNumber(newValue, phoneValue.country)
    const fullNumber = allowInternationalFormat 
      ? toInternationalFormat(phoneValue.country, newValue)
      : newValue
    
    onChange?.({
      country: phoneValue.country,
      number: newValue,
      fullNumber,
      isValid: valid
    })
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!autoFormat) return
    
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const cleaned = pastedText.replace(/\D/g, '')
    const formatted = formatPhoneNumber(cleaned, selectedCountry.format)
    
    const valid = validatePhoneNumber(formatted, phoneValue.country)
    const fullNumber = allowInternationalFormat 
      ? toInternationalFormat(phoneValue.country, formatted)
      : formatted
    
    onChange?.({
      country: phoneValue.country,
      number: formatted,
      fullNumber,
      isValid: valid
    })
  }

  return (
    <div ref={ref} className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor="phone-number" className={labelClassName}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      
      <div className="flex gap-2">
        {/* Ülke Seçici */}
        <Select
          value={phoneValue.country}
          onValueChange={handleCountryChange}
          disabled={disabled}
        >
          <SelectTrigger 
            className={cn(
              "w-[140px]",
              error && "border-destructive",
              selectClassName
            )}
          >
            <SelectValue>
              <div className="flex items-center gap-2">
                {showFlags && <span className="text-lg">{selectedCountry.flag}</span>}
                {showDialCode && <span className="text-sm">{selectedCountry.dialCode}</span>}
                {!showFlags && !showDialCode && <span className="text-sm">{selectedCountry.code}</span>}
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {countryList.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                <div className="flex items-center gap-2">
                  {showFlags && <span className="text-lg">{country.flag}</span>}
                  <span className="text-sm">{country.name}</span>
                  {showDialCode && <span className="text-muted-foreground text-sm">{country.dialCode}</span>}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Telefon Numarası Input */}
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            id="phone-number"
            type="tel"
            value={phoneValue.number}
            onChange={handleNumberChange}
            onPaste={handlePaste}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={selectedCountry?.format?.replace(/x/g, '•') || 'Enter phone number'}
            className={cn(
              "pr-10",
              error && "border-destructive",
              inputClassName
            )}
            disabled={disabled}
          />
          
          {/* Doğrulama İkonu */}
          {showValidationIcon && phoneValue.number && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <AnimatePresence mode="wait">
                {isValid ? (
                  <motion.div
                    key="valid"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Check className="w-4 h-4 text-green-500" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="invalid"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-4 h-4 text-destructive" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Helper Text veya Error */}
      {(error || helperText) && (
        <AnimatePresence mode="wait">
          {error ? (
            <motion.p
              key="error"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={cn("text-sm text-destructive", errorClassName)}
            >
              {error}
            </motion.p>
          ) : (
            <motion.p
              key="helper"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="text-sm text-muted-foreground"
            >
              {helperText}
            </motion.p>
          )}
        </AnimatePresence>
      )}

      {/* Uluslararası Format Gösterimi */}
      {allowInternationalFormat && phoneValue.number && isValid && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Globe className="w-4 h-4" />
          <span>International: {toInternationalFormat(phoneValue.country, phoneValue.number)}</span>
        </motion.div>
      )}
    </div>
  )
})

PhoneNumberInputInternal.displayName = "PhoneNumberInputInternal"

// Export with license check
export const MoonUIPhoneNumberInputPro = React.forwardRef<HTMLDivElement, PhoneNumberInputProps>(
  (props, ref) => {
    const { hasProAccess, isLoading } = useSubscription()
    
    if (!isLoading && !hasProAccess) {
      return (
        <Card className={cn("w-full max-w-md", props.className)}>
          <CardContent className="py-12 text-center">
            <div className="space-y-4">
              <div className="rounded-full bg-purple-100 dark:bg-purple-900/30 p-3 w-fit mx-auto">
                <Lock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Pro Feature</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Phone Number Input is available exclusively to MoonUI Pro subscribers.
                </p>
                <a href="/pricing">
                  <Button size="sm">
                    <Sparkles className="mr-2 h-4 w-4" />
                    Upgrade to Pro
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    return <PhoneNumberInputInternal {...props} ref={ref} />
  }
)

MoonUIPhoneNumberInputPro.displayName = "MoonUIPhoneNumberInputPro"

// Alias export
export const PhoneNumberInput = MoonUIPhoneNumberInputPro
export type MoonUIPhoneNumberInputProProps = PhoneNumberInputProps

// Ülke listesini de export edelim
export { countries as phoneCountries }

// Basit versiyon
export { MoonUIPhoneNumberInputSimple } from "./phone-number-input-simple"